/**
 * @lines98/client/lib/screen-snapshot — ОБОБЩЁННЫЙ per-player snapshot-кэш для
 * МГНОВЕННОЙ отрисовки динамики любого экрана ДО ресолва сети (instant-ui-hydration §3).
 *
 * ЗАЧЕМ / WHY THIS EXISTS (instant-hydration на лидерборд+профиль 2026-06-10):
 * `lib/meta-snapshot` решил «передёргивание» Мастерской/Профиля через синхронный снимок
 * мета-динамики; лидерборду нужен ТАКОЙ ЖЕ приём, но на СВОЙ источник (страницы борда —
 * не `/meta/state`). Чтобы не плодить второй кэш-движок (copy-paste persistence/изоляции/
 * type-guard), эта фабрика — ЕДИНЫЙ «ствол» снимка: `createScreenSnapshot<T>(...)` отдаёт
 * `{ load, save, clear }` для ЛЮБОГО typed-снимка `T`. `meta-snapshot` и
 * `leaderboard-snapshot` — тонкие «ветки» поверх неё (модель «ствол → ветки», CLAUDE.md /
 * code-structure §1). Будущие surface'ы переиспользуют ту же фабрику, а не копию.
 *
 * **Инвариант (одинаков для всех веток, instant-hydration §3 + анти-чит):** снимок —
 * ИСКЛЮЧИТЕЛЬНО UX-проекция для мгновенного показа. Истина — всегда следующий серверный
 * ответ (reconcile перетирает кэш server-truth'ом). Кэш НИКОГДА не вход для проверок/
 * начислений/верификации; движок/replayScore его не импортируют. Лидерборд read-only
 * (показ), мета-балансы display-only — кэш ничего не «решает».
 *
 * **Ключ по идентичности игрока (privacy):** полный ключ = `${keyPrefix}${playerId}` (см.
 * `services/identity`). Аккаунт A не видит снимок B; гость (нет identity) → пусто (no-op).
 * Версия схемы `version` в payload: при несовпадении снимок считается битым и отбрасывается
 * (forward-compat — старый снимок не «протекает» в новую форму).
 *
 * **Backend: localStorage (синхронный)** — снимок читается СИНХРОННО при mount, чтобы
 * нарисовать динамику в тот же тик, ДО любого сетевого/IDB-await. Все операции defensive
 * (catch+ignore): сбой кэша мягко деградирует UX, не ломает экран и не блокирует server-truth.
 *
 * Cross-references:
 *   - lib/meta-snapshot.ts (ветка: мета-динамика игрока — Мастерская/Профиль).
 *   - lib/leaderboard-snapshot.ts (ветка: последняя страница борда — Лидерборд).
 *   - services/identity.ts (getCurrent().playerId — ключ кэша).
 *   - .claude/skills/instant-ui-hydration/SKILL.md §3 (паттерн снимка).
 */

import { getCurrent } from '../services/identity.js';

// =============================================================================
// Generic envelope — оборачивает payload `T` версией схемы (для guard'а).
// =============================================================================

/**
 * Внутренняя обёртка снимка в storage: `{ v, data }`. `v` — версия схемы ветки
 * (несовпадение → отбрасываем). `data` — typed-payload ветки (`T`). Ветка работает с
 * `T` напрямую; обёртку/версию/ключ/изоляцию/persistence держит этот «ствол».
 */
interface SnapshotEnvelope<T> {
  readonly v: number;
  readonly data: T;
}

// =============================================================================
// Factory
// =============================================================================

/** Параметры одной «ветки» снимка. */
export interface ScreenSnapshotConfig<T> {
  /**
   * Префикс ключа storage (уникален на ветку, напр. `'lines98.meta.snapshot.v1.'`).
   * Полный ключ = `${keyPrefix}${playerId}`. Версионируй префикс при breaking-смене
   * формы (как делали ветки), чтобы старые ключи не путались с новыми.
   */
  readonly keyPrefix: string;
  /** Версия схемы payload — пишется в обёртку, проверяется при чтении. */
  readonly version: number;
  /**
   * Type-guard payload: вернуть `true`, если `value` — валидный `T`. Битый/чужой формы
   * payload → `load` отдаёт `null` (не падает). Ветка знает форму своего `T`.
   */
  readonly isValid: (value: unknown) => value is T;
}

/** Хендл одной ветки снимка: синхронные read/write/clear для текущего игрока. */
export interface ScreenSnapshotHandle<T> {
  /**
   * Прочитать снимок ТЕКУЩЕГО игрока СИНХРОННО (для мгновенного рендера при mount).
   * `null`, если: гость (нет identity), снимка нет, версия/форма не совпали (битый),
   * либо localStorage недоступен. Чистый read — server-truth остаётся истиной.
   */
  load(): T | null;
  /**
   * Сохранить снимок текущего игрока (после успешного серверного ответа / ресинка —
   * кэш не устаревает грубо). No-op для гостя (нет ключа). Defensive: сбой записи
   * (quota / приватный режим) проглатывается — это UX-кэш, не истина.
   */
  save(data: T): void;
  /** Удалить снимок текущего игрока (явный сброс / тесты). No-op для гостя. */
  clear(): void;
}

/**
 * Создать ветку снимка `T` поверх общего движка. Все ветки (meta / leaderboard / будущие)
 * получают одинаковые гарантии: per-player изоляцию, версионирование, синхронный mgновенный
 * read, defensive-persistence — БЕЗ дублирования этой логики по веткам.
 */
export function createScreenSnapshot<T>(config: ScreenSnapshotConfig<T>): ScreenSnapshotHandle<T> {
  const { keyPrefix, version, isValid } = config;

  /** Ключ снимка текущего игрока, либо null (гость / нет identity → не читаем/не пишем). */
  function currentKey(): string | null {
    const id = getCurrent()?.playerId;
    return id ? `${keyPrefix}${id}` : null;
  }

  return {
    load(): T | null {
      const key = currentKey();
      if (!key) return null;
      try {
        const raw = typeof window !== 'undefined' ? window.localStorage?.getItem(key) : null;
        if (!raw) return null;
        const parsed = JSON.parse(raw) as unknown;
        if (!isEnvelope(parsed) || parsed.v !== version) return null;
        return isValid(parsed.data) ? parsed.data : null;
      } catch {
        return null;
      }
    },

    save(data: T): void {
      const key = currentKey();
      if (!key) return;
      try {
        if (typeof window !== 'undefined') {
          const envelope: SnapshotEnvelope<T> = { v: version, data };
          window.localStorage?.setItem(key, JSON.stringify(envelope));
        }
      } catch {
        // quota / private-mode — non-fatal; экран продолжает корректно (в памяти).
      }
    },

    clear(): void {
      const key = currentKey();
      if (!key) return;
      try {
        if (typeof window !== 'undefined') {
          window.localStorage?.removeItem(key);
        }
      } catch {
        // ignore
      }
    },
  };
}

/** Грубый guard обёртки (наличие `v: number` + поля `data`); форму `data` чекает ветка. */
function isEnvelope(value: unknown): value is SnapshotEnvelope<unknown> {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { v?: unknown }).v === 'number' &&
    'data' in value
  );
}
